/**
 * useVariantStyles Hook
 * Gets variant-specific styles based on theme and state
 */

import { useMemo } from 'react';
import { useTheme, alpha } from '@mui/material';
import type { Theme } from '@mui/material';
import type { ButtonVariant, VariantStyles } from '../types';

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for getting variant-specific styles
 */
export function useVariantStyles(variant: ButtonVariant, isSelected: boolean): VariantStyles {
  const theme = useTheme();

  return useMemo(() => {
    return getVariantStyles(theme, variant, isSelected);
  }, [isSelected, theme, variant]);
}

// ============================================================================
// PURE FUNCTION
// ============================================================================

/**
 * Pure function for variant style calculation
 * Exported for testing and utility usage
 */
export function getVariantStyles(
  theme: Theme,
  variant: ButtonVariant,
  selected: boolean,
): VariantStyles {
  const { palette, shadows } = theme;
  const mode = palette.mode;

  // Selected state colors
  if (selected) {
    return {
      activeBackgroundColor: alpha(palette.primary.main, 0.85),
      backgroundColor: palette.primary.main,
      boxShadow: variant === 'elevated' ? shadows[2] : undefined,
      color: palette.primary.contrastText,
      disabledBackgroundColor: alpha(palette.action.disabled, 0.12),
      disabledColor: palette.action.disabled,
      hoverBackgroundColor: alpha(palette.primary.main, 0.92),
    };
  }

  switch (variant) {
    case 'elevated':
      return {
        activeBackgroundColor: mode === 'light' ? palette.grey[200] : palette.grey[600],
        backgroundColor: mode === 'light' ? palette.grey[50] : palette.grey[800],
        boxShadow: shadows[1],
        color: palette.primary.main,
        disabledBackgroundColor: alpha(palette.action.disabled, 0.12),
        disabledColor: palette.action.disabled,
        hoverBackgroundColor: mode === 'light' ? palette.grey[100] : palette.grey[700],
        hoverBoxShadow: shadows[2],
      };

    case 'filled':
      return {
        activeBackgroundColor: alpha(palette.primary.main, 0.85),
        backgroundColor: palette.primary.main,
        color: palette.primary.contrastText,
        disabledBackgroundColor: alpha(palette.action.disabled, 0.12),
        disabledColor: palette.action.disabled,
        hoverBackgroundColor: alpha(palette.primary.main, 0.92),
      };

    case 'outlined':
      return {
        activeBackgroundColor: alpha(palette.primary.main, 0.08),
        backgroundColor: 'transparent',
        border: `1px solid ${palette.divider}`,
        color: palette.primary.main,
        disabledBackgroundColor: 'transparent',
        disabledColor: palette.action.disabled,
        hoverBackgroundColor: alpha(palette.primary.main, 0.04),
      };

    case 'tonal':
      return {
        activeBackgroundColor: alpha(palette.primary.main, 0.24),
        backgroundColor: alpha(palette.primary.main, 0.12),
        color: palette.primary.dark,
        disabledBackgroundColor: alpha(palette.action.disabled, 0.08),
        disabledColor: palette.action.disabled,
        hoverBackgroundColor: alpha(palette.primary.main, 0.18),
      };

    case 'text':
      return {
        activeBackgroundColor: alpha(palette.primary.main, 0.08),
        backgroundColor: 'transparent',
        color: palette.primary.main,
        disabledBackgroundColor: 'transparent',
        disabledColor: palette.action.disabled,
        hoverBackgroundColor: alpha(palette.primary.main, 0.04),
      };

    case 'danger':
      return {
        color: palette.error.contrastText,
        backgroundColor: palette.error.main,
        hoverBackgroundColor: palette.error.dark,
        activeBackgroundColor: palette.error.dark,
        disabledColor: palette.action.disabled,
        disabledBackgroundColor: palette.action.disabledBackground,
      };

    default: {
      // Exhaustiveness check - TypeScript will error if a variant is missing
      const NEVER_VARIANT: never = variant;
      throw new Error(`Unhandled button variant: ${NEVER_VARIANT}`);
    }
  }
}

export default useVariantStyles;
